export const KEYSTONE_PACE_SCHEMA_VERSION = 3;
export const KEYSTONE_PACE_ALGORITHM_VERSION = 'clear-time-and-ilvl-percent-ranks-v3';
export const KEYSTONE_PACE_CURRENT_QUANTILE_COUNT = 1000;
export const KEYSTONE_PACE_HISTORICAL_QUANTILE_COUNT = 100;
export const KEYSTONE_PACE_REGULAR_MIN_POPULATION = 10;
export const KEYSTONE_PACE_ITEM_LEVEL_MIN_POPULATION = 10;
export const KEYSTONE_PACE_ITEM_LEVEL_BRACKET_SIZE = 2;
// The Pace Targets percentile a bare URL means, so link builders strip it and the page assumes it.
export const KEYSTONE_PACE_DEFAULT_TARGET_PERCENTILE = 75;
// Percentile 0 is not a target anyone can aim at, so the slider and every parser floor here.
export const KEYSTONE_PACE_MIN_TARGET_PERCENTILE = 1;
export const KEYSTONE_PACE_WORLD_REGIONS = ['us', 'eu', 'kr', 'tw', 'cn'] as const;

export type TKeystonePaceDataset = 'current' | 'historical';
export type TKeystonePaceScopeKey = 'world' | `region:${number}` | `subregion:${number}`;

export type TKeystonePaceWorldScope = {
  type: 'world';
  id: null;
  slug: 'world';
};

export type TKeystonePaceCatalogScope = {
  type: 'region' | 'subregion';
  id: number;
  slug: string;
};

export type TKeystonePaceScope = TKeystonePaceWorldScope | TKeystonePaceCatalogScope;

export const KEYSTONE_PACE_WORLD_SCOPE: TKeystonePaceWorldScope = {
  type: 'world',
  id: null,
  slug: 'world'
};

export function getKeystonePaceScopeKey(scope: TKeystonePaceScope): TKeystonePaceScopeKey {
  return scope.type === 'world' ? 'world' : `${scope.type}:${scope.id}`;
}

export function parseKeystonePaceScopeKey(
  value: string
): { type: 'world'; id: null } | { type: 'region' | 'subregion'; id: number } | null {
  if (value === 'world') {
    return { type: 'world', id: null };
  }

  const match = /^(region|subregion):([1-9]\d*)$/.exec(value);
  if (!match) {
    return null;
  }
  const id = Number(match[2]);
  return Number.isSafeInteger(id) ? { type: match[1] as 'region' | 'subregion', id } : null;
}

export type TKeystonePaceItemLevelBracket = {
  average: number;
  min: number;
  maxExclusive: number;
};

export function getKeystonePaceItemLevelBracket(itemLevelMean: unknown): TKeystonePaceItemLevelBracket | null {
  if (itemLevelMean === null || itemLevelMean === undefined || itemLevelMean === '') {
    return null;
  }

  const average = Number(itemLevelMean);
  if (!Number.isFinite(average) || average <= 0) {
    return null;
  }

  const min = Math.floor(average / KEYSTONE_PACE_ITEM_LEVEL_BRACKET_SIZE) * KEYSTONE_PACE_ITEM_LEVEL_BRACKET_SIZE;
  return {
    average,
    min,
    maxExclusive: min + KEYSTONE_PACE_ITEM_LEVEL_BRACKET_SIZE
  };
}

/** Per-quantile population summary emitted into generated pace artifacts. */
export type TKeystonePaceQuantileEntry = {
  quantile: number;
  quantileMinValue: number;
  quantilePopulationCount: number;
  quantilePopulationFraction: number;
  totalPopulationCount: number;
};

/** [negated clear time, number of runs strictly slower than this threshold]. */
export type TKeystonePaceRankThreshold = readonly [scoreValue: number, strictlySlowerCount: number];

export type TKeystonePaceItemLevelDistribution = {
  minItemLevel: number;
  populationCount: number;
  distinctClearTimeCount: number;
  curve: TKeystonePaceCurve;
};

export type TKeystonePaceLevelDistribution = {
  keyLevel: number;
  populationCount: number;
  distinctClearTimeCount: number;
  curve: TKeystonePaceCurve;
  itemLevelBrackets: TKeystonePaceItemLevelDistribution[];
};

/**
 * Columnar rank curve. `scores` holds negated clear-time thresholds in strictly increasing order
 * (fastest last), including fractional quantile thresholds; `slower[i]` is the number of runs
 * strictly slower than `scores[i]`, so it is nondecreasing and `slower[0] === 0`. Equal-rank runs
 * retain their two score endpoints for interpolation but drop interior points. Rank lookups and
 * interpolation geometry remain identical to the sampled rank thresholds.
 */
export type TKeystonePaceCurve = {
  scores: number[];
  slower: number[];
};

export type TKeystonePaceSourceIdentity = {
  partitionEventId: number | null;
  highWaterRunId: number;
  scannedRows: number;
};

export type TKeystonePaceCohortSourceSummary = {
  acceptedRows: number;
  acceptedRowsByRegion: Record<string, number>;
  itemLevelEligibleRows: number;
  itemLevelEligibleRowsByRegion: Record<string, number>;
  itemLevelMaterializedRows: number;
  itemLevelThinRows: number;
};

export type TKeystonePaceSourceSummary = TKeystonePaceSourceIdentity & TKeystonePaceCohortSourceSummary;

export type TKeystonePaceArtifact = {
  schemaVersion: typeof KEYSTONE_PACE_SCHEMA_VERSION;
  algorithmVersion: typeof KEYSTONE_PACE_ALGORITHM_VERSION;
  season: string;
  dataset: TKeystonePaceDataset;
  period: number | null;
  generation: string;
  scope: TKeystonePaceScope;
  zoneId: number;
  generatedAt: string;
  levels: TKeystonePaceLevelDistribution[];
};

/**
 * One gzipped field of a serving-artifact Redis hash: a single level or bracket curve.
 * `minItemLevel: null` marks a regular (overall) curve. Corruption is caught by the gzip CRC and
 * this generation/scope/zone envelope rather than a sha256, so a bad field degrades to one S3 re-read.
 */
export type TKeystonePaceCurveSlice = {
  v: typeof KEYSTONE_PACE_SCHEMA_VERSION;
  generation: string;
  scopeKey: TKeystonePaceScopeKey;
  zoneId: number;
  generatedAt: string;
  keyLevel: number;
  minItemLevel: number | null;
  populationCount: number;
  distinctClearTimeCount: number;
  curve: TKeystonePaceCurve;
};

export type TKeystonePaceIndexBracket = {
  min: number;
  populationCount: number;
  distinctClearTimeCount: number;
};

export type TKeystonePaceIndexLevel = {
  keyLevel: number;
  populationCount: number;
  distinctClearTimeCount: number;
  brackets: TKeystonePaceIndexBracket[];
};

/**
 * The `idx` field of a serving-artifact Redis hash: the artifact's level/bracket catalog. Its
 * presence is what distinguishes "curve absent from the data" from "hash not yet exploded".
 */
export type TKeystonePaceArtifactIndex = {
  v: typeof KEYSTONE_PACE_SCHEMA_VERSION;
  generation: string;
  scopeKey: TKeystonePaceScopeKey;
  zoneId: number;
  generatedAt: string;
  levels: TKeystonePaceIndexLevel[];
};

export type TKeystonePaceDatasetSummary = {
  dataset: TKeystonePaceDataset;
  period: number | null;
  generation: string;
  generatedAt: string;
  finalized: boolean;
  scopeKeys: TKeystonePaceScopeKey[];
  zoneIds: number[];
};

/**
 * Small per-release digest served to the request fast path so processes never retain a parsed
 * full release manifest. Existence hints only: the explode path still consults the release's
 * per-dataset manifest chunk (authoritative artifact references) before touching S3.
 */
export type TKeystonePaceReleaseSummary = {
  v: typeof KEYSTONE_PACE_SCHEMA_VERSION;
  season: string;
  releaseId: string;
  createdAt: string;
  current: TKeystonePaceDatasetSummary | null;
  historical: Record<string, TKeystonePaceDatasetSummary>;
  finalizedPeriods: number[];
};

export type TKeystonePaceArtifactReference = {
  zoneId: number;
  key: string;
  sha256: string;
  uncompressedSha256: string;
  compressedBytes: number;
  uncompressedBytes: number;
  levelCount: number;
  population: number;
  itemLevelBracketCount: number;
  itemLevelStoredPointCount: number;
  itemLevelPopulation: number;
};

export type TKeystonePaceCohortManifest = {
  scope: TKeystonePaceScope;
  source: TKeystonePaceCohortSourceSummary;
  artifacts: Record<string, TKeystonePaceArtifactReference>;
};

export type TKeystonePaceDatasetManifest = {
  dataset: TKeystonePaceDataset;
  period: number | null;
  generation: string;
  generatedAt: string;
  finalized: boolean;
  source: TKeystonePaceSourceIdentity;
  cohorts: Record<TKeystonePaceScopeKey, TKeystonePaceCohortManifest>;
};

export type TKeystonePaceReleaseManifest = {
  schemaVersion: typeof KEYSTONE_PACE_SCHEMA_VERSION;
  algorithmVersion: typeof KEYSTONE_PACE_ALGORITHM_VERSION;
  season: string;
  releaseId: string;
  createdAt: string;
  current: TKeystonePaceDatasetManifest | null;
  historical: Record<string, TKeystonePaceDatasetManifest>;
  finalizedPeriods: number[];
};

export type TKeystonePaceActivePointer = {
  schemaVersion: typeof KEYSTONE_PACE_SCHEMA_VERSION;
  season: string;
  releaseId: string;
  updatedAt: string;
};

export type TKeystonePaceStanding = {
  percentile: number;
  populationCount: number;
  generatedAt: string;
};

export type TKeystonePaceHistoricalStanding = TKeystonePaceStanding & {
  period: number;
  locked: boolean;
};

export type TKeystonePaceFacet = {
  current: TKeystonePaceStanding | null;
  historical: TKeystonePaceHistoricalStanding | null;
  itemLevel: {
    average: number;
    min: number;
    maxExclusive: number;
    current: TKeystonePaceStanding | null;
    historical: TKeystonePaceHistoricalStanding | null;
  } | null;
};

export type TKeystonePaceScopedFacet = TKeystonePaceFacet & {
  scope: TKeystonePaceScope;
};

export type TKeystonePace = TKeystonePaceFacet & {
  scopes: {
    world: TKeystonePaceScopedFacet;
    region: TKeystonePaceScopedFacet | null;
    subRegions: TKeystonePaceScopedFacet[];
  };
};

export type TKeystonePaceTargetMetric = 'overall' | 'party-item-level';

export type TKeystonePaceTargetPoint = {
  percentile: number;
  clearTimeMs: number;
};

export type TKeystonePaceItemLevelBracketSummary = {
  min: number;
  maxExclusive: number;
  populationCount: number;
};

export type TKeystonePaceTargets = {
  season: string;
  zoneId: number;
  keyLevel: number;
  scope: TKeystonePaceScope;
  metric: TKeystonePaceTargetMetric;
  itemLevelBracket: TKeystonePaceItemLevelBracket | null;
  availableItemLevelBrackets: TKeystonePaceItemLevelBracketSummary[];
  generatedAt: string;
  populationCount: number;
  distinctClearTimeCount: number;
  points: TKeystonePaceTargetPoint[];
};

export type TKeystonePaceTargetsResponse = {
  paceTargets: TKeystonePaceTargets | null;
  availableKeyLevels: number[];
};

export function getKeystonePaceScopedFacets(pace: TKeystonePace): TKeystonePaceScopedFacet[] {
  return [pace.scopes.world, ...(pace.scopes.region ? [pace.scopes.region] : []), ...pace.scopes.subRegions];
}

export function getKeystonePaceScopedFacet(
  pace: TKeystonePace,
  scopeKey: TKeystonePaceScopeKey
): TKeystonePaceScopedFacet | null {
  if (scopeKey === 'world') {
    return pace.scopes.world;
  }
  if (pace.scopes.region && getKeystonePaceScopeKey(pace.scopes.region.scope) === scopeKey) {
    return pace.scopes.region;
  }
  return pace.scopes.subRegions.find((entry) => getKeystonePaceScopeKey(entry.scope) === scopeKey) || null;
}
